import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography, TablePagination } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GridColDef, GridCellParams, DataGridPro } from '@mui/x-data-grid-pro';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as invoiceSlice from '../../../data/invoice.slice';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { InvoiceRow } from '../../../data/models/invoice.model';
import { useNavigate } from 'react-router-dom';
import DateUtils from '../../../services/DateUtils';
import { DateTime } from 'luxon';
const PREFIX = 'InvoiceDetail';

const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  content: `${PREFIX}-content`,
  title: `${PREFIX}-title`,
  subTitle: `${PREFIX}-subTitle`,
  description: `${PREFIX}-description`,
  subDescription: `${PREFIX}-subDescription`,
  grid: `${PREFIX}-grid`,
  cellText: `${PREFIX}-cellText`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: 13,
    color: 'rgba(1, 16, 18, 0.90)',
  },
  [`& .${classes.header}`]: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: theme.spacing(1),
  },
  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingBottom: theme.spacing(1),
  },
  [`& .${classes.title}`]: {
    fontSize: 30,
    fontWeight: 700,
    fontFamily: 'Bitter',
    lineHeight: '40px',
    letterSpacing: '0.25px',
  },
  [`& .${classes.subTitle}`]: {
    fontSize: 21,
    fontWeight: 700,
    fontFamily: 'Bitter',
    lineHeight: '32px',
    letterSpacing: '0.25px',
  },
  [`& .${classes.subDescription}`]: {
    fontSize: 12,
    fontFamily: 'Source Sans Pro',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '266%',
    letterSpacing: '1px',
    textTransform: 'uppercase',
  },
  [`& .${classes.description}`]: {
    fontSize: theme.spacing(2),
    fontFamily: 'Source Sans Pro',
  },
  [`& .${classes.grid}`]: {
    border: 'none',
    '.MuiDataGrid-columnHeaderTitle': {
      color: 'rgba(1, 16, 18, 0.90)',
      fontFamily: 'Source Sans Pro',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: 600,
      lineHeight: '24px',
      letterSpacing: '0.17px',
    },

    '& .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-columnHeaderTitleContent, & .MuiDataGrid-columnHeaderTitleContainerContent, & .MuiDataGrid-columnHeaderTitle':
      {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
      },
    '& .MuiDataGrid-columnSeparator': {
      display: 'none',
    },

    '& .MuiDataGrid-columnHeader--last': {
      paddingRight: 0,
    },

    [`& .MuiDataGrid-row`]: {
      cursor: 'pointer',
    },

    [`& .MuiDataGrid-columnHeader`]: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'rgba(1, 16, 18, 0.90)',
      fontFamily: 'Source Sans Pro',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: 600,
      lineHeight: '24px',
      letterSpacing: '0.17px',
    },

    [`& .MuiDataGrid-cell`]: {
      display: 'flex',
      alignItems: 'center',
    },
    [`& .${classes.cellText}`]: {
      color: 'rgba(1, 16, 18, 0.90)',
      fontFamily: 'Source Sans Pro',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '143%',
      letterSpacing: '0.17px',
    },
  },
}));

export default function InvoiceDetail({ providerId }: { providerId: number }) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  React.useEffect(() => {
    if (providerId) {
      dispatch(invoiceSlice.fetchInvoicesAsync(providerId));
    }
  }, [providerId]);

  function getTwoWeeksLaterFriday(invoiceDate: string): string {
    const date = DateTime.fromISO(invoiceDate);

    const isWeekend = date.weekday === 6 || date.weekday === 7;

    const baseFriday = date.startOf('week').plus({ days: isWeekend ? 12 : 5 });
    const twoWeeksLaterFriday = baseFriday.plus({ days: 14 });

    return twoWeeksLaterFriday.toFormat('MM/dd/yyyy');
  }

  const invoices = useAppSelector(invoiceSlice.selectInvoices);
  const invoiceList: InvoiceRow[] = invoices.invoices.map((invoice) => ({
    id: invoice.providerInvoiceID,
    invoiceNumber: invoice.invoiceNumber,
    amount: invoice.invoiceAmount,
    paymentOn: getTwoWeeksLaterFriday(invoice.invoiceDate),
    createdOn: DateUtils.formatDate(DateTime.fromISO(invoice.createdOnUTC)),
    totalTripLegs: invoice.totalTripLegs,
    billingStatusID: invoice.billingStatusID,
    billingStatusBC: invoice.billingStatusBC,
  }));

  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedInvoiceList = invoiceList.slice(startIndex, endIndex);

  const handleInvoiceDetailClick = () => {
    navigate(`/invoices`);
  };

  const renderInvoiceID = (params: GridCellParams): React.ReactElement => {
    const { row } = params;
    return (
      <Box>
        <Typography className={classes.cellText}>
          {row.invoiceNumber}
        </Typography>
      </Box>
    );
  };

  const renderAmount = (params: GridCellParams): React.ReactElement => {
    const { row } = params;
    return (
      <Box>
        <Typography className={classes.cellText}>{row.amount}</Typography>
      </Box>
    );
  };

  const renderPaymentOn = (params: GridCellParams): React.ReactElement => {
    const { row } = params;
    return (
      <Box>
        <Typography className={classes.cellText}>{row.paymentOn}</Typography>
      </Box>
    );
  };

  const renderCenterHeader = ({
    colDef: { headerName },
  }: {
    colDef: { headerName?: string };
  }): React.ReactNode => {
    return (
      <Box
        style={{
          textAlign: 'center',
          width: '100%',
          height: '1',
        }}>
        {headerName}
      </Box>
    );
  };

  const columns: GridColDef<InvoiceRow>[] = [
    {
      field: 'invoiceNumber',
      headerName: t('status.id'),
      flex: 1,
      align: 'left',
      renderCell: renderInvoiceID,
    },
    {
      field: 'amount',
      headerName: t('status.amount'),
      flex: 0.75,
      align: 'center',
      renderCell: renderAmount,
      renderHeader: (params) => renderCenterHeader(params),
    },
    {
      field: 'paymentOn',
      headerName: t('status.paymentOn'),
      flex: 1,
      align: 'right',
      renderCell: renderPaymentOn,
      renderHeader: renderCenterHeader,
    },
  ];

  return (
    <StyledBox className={classes.container}>
      <Box className={classes.header}>
        <Typography className={classes.title} variant="h2">
          {t('status.invoice')}
        </Typography>
      </Box>
      <DataGridPro
        rows={paginatedInvoiceList}
        onRowClick={() => {
          handleInvoiceDetailClick();
        }}
        columns={columns}
        className={classes.grid}
        disableRowSelectionOnClick
        hideFooterSelectedRowCount
        hideFooterRowCount
        hideFooter
        rowHeight={56}
        disableColumnMenu={true}
        disableColumnSorting={true}
        disableColumnResize={true}
      />
      <TablePagination
        component="div"
        count={invoiceList.length}
        page={page - 1}
        onPageChange={(event, page) => setPage(page + 1)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(event) => {
          setPageSize(Number.parseInt(event.target.value, 10));
          setPage(1);
        }}
        labelRowsPerPage=""
      />
    </StyledBox>
  );
}
